/**
 * Wes agent settings — voice mode, model, enable, floating launcher.
 * Route: /prioritization/settings/wes-agent
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, AlertCircle, ChevronLeft } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { WesIcon } from '../../../components/harry/WesIcon';
import { useWesStore } from '../../../stores/prioritization/wesStore';
import { useHarryStore } from '../../../stores/prioritization/harryStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  defaultWesSystemPrompt,
  unpackWesSettings,
  type WesVoiceMode,
} from '../../../lib/wes/wesPrompts';
import { CURRENT_MODELS, DEFAULT_MODEL } from '../../../../supabase/functions/_shared/modelRequest.ts';

const MODEL_OPTIONS = CURRENT_MODELS.map((m) => m.id);

export default function WesAgent() {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const addNotification = useUIStore((s) => s.addNotification);

  const config = useWesStore((s) => s.config);
  const voiceMode = useWesStore((s) => s.voiceMode);
  const uiPrefs = useHarryStore((s) => s.uiPrefs);
  const isLoading = useWesStore((s) => s.isLoading);
  const isSaving = useWesStore((s) => s.isSaving);
  const fetchConfig = useWesStore((s) => s.fetchConfig);
  const saveConfig = useWesStore((s) => s.saveConfig);
  const setUiPrefs = useHarryStore((s) => s.setUiPrefs);

  const [promptDraft, setPromptDraft] = useState('');
  const [promptDirty, setPromptDirty] = useState(false);

  useEffect(() => {
    if (workspaceId) void fetchConfig(workspaceId);
  }, [workspaceId, fetchConfig]);

  useEffect(() => {
    if (!config) return;
    const { promptBody } = unpackWesSettings(config.systemPrompt);
    setPromptDraft(promptBody || defaultWesSystemPrompt(voiceMode));
    setPromptDirty(false);
  }, [config, voiceMode]);

  const handleVoiceChange = async (mode: WesVoiceMode) => {
    if (!workspaceId) return;
    const nextPrompt = promptDirty ? promptDraft : defaultWesSystemPrompt(mode);
    if (!promptDirty) setPromptDraft(nextPrompt);
    const result = await saveConfig(workspaceId, { voiceMode: mode, promptBody: nextPrompt }, userId);
    if (result.success) {
      addNotification({ type: 'success', message: mode === 'wes' ? 'Wes mode on' : 'Normal mode on' });
    }
  };

  const handleSave = async () => {
    if (!workspaceId || !config) return;
    const result = await saveConfig(workspaceId, {
      enabled: config.enabled,
      model: config.model,
      voiceMode,
      promptBody: promptDraft,
    }, userId);
    if (result.success) {
      setPromptDirty(false);
      addNotification({ type: 'success', message: 'Wes settings saved' });
    } else {
      addNotification({ type: 'error', message: result.error ?? 'Save failed' });
    }
  };

  if (isLoading && !config) {
    return (
      <div className="flex items-center justify-center py-20 text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Link to="/prioritization/settings/agents" className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] no-underline">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to Agents
      </Link>

      <PageHeader
        icon={<WesIcon size={18} />}
        iconBg="bg-[var(--slate-dim)]"
        title="Wes"
        subtitle="Co-founder guide — routes you to the right job and answers in plain language."
      />

      <div className="card space-y-5">
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <p className="text-sm font-medium text-[var(--text-pri)] m-0">Enabled</p>
            <p className="text-xs text-[var(--text-ter)] m-0 mt-0.5">When off, Wes uses keyword routing only (no LLM).</p>
          </div>
          <input
            type="checkbox"
            checked={config?.enabled ?? true}
            onChange={(e) => workspaceId && void saveConfig(workspaceId, { enabled: e.target.checked }, userId)}
            className="h-4 w-4 rounded border-[var(--rule)]"
          />
        </label>

        <div>
          <p className="text-sm font-medium text-[var(--text-pri)] mb-2">Voice</p>
          <div className="flex gap-2">
            {([
              { id: 'normal' as const, label: 'Normal', hint: 'Concise product guide' },
              { id: 'wes' as const, label: 'Wes mode', hint: 'Sounds like Wes Bright' },
            ]).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => void handleVoiceChange(opt.id)}
                className={`flex-1  border px-3 py-2 text-left transition-colors ${
                  voiceMode === opt.id
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-indigo-200'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <span className="block text-sm font-medium text-[var(--text-pri)]">{opt.label}</span>
                <span className="block text-xs text-[var(--text-ter)]">{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-pri)] mb-1">Model</label>
          <select
            value={config?.model ?? DEFAULT_MODEL}
            onChange={(e) => workspaceId && void saveConfig(workspaceId, { model: e.target.value }, userId)}
            className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <p className="text-sm font-medium text-[var(--text-pri)] m-0">Floating launcher</p>
            <p className="text-xs text-[var(--text-ter)] m-0 mt-0.5">
              When Guides owns the corner, the lower-right button opens this help panel (Navigator / Harry / Wes).
              Header help icon always opens Guides. Switch corner ownership on Agents settings or inside either panel.
            </p>
          </div>
          <input
            type="checkbox"
            checked={uiPrefs.showFloatingLauncher}
            onChange={(e) => setUiPrefs(userId, { showFloatingLauncher: e.target.checked })}
            className="h-4 w-4 rounded border-[var(--rule)]"
          />
        </label>

        <div>
          <label className="block text-sm font-medium text-[var(--text-pri)] mb-1">System prompt</label>
          <p className="text-xs text-[var(--text-ter)] mb-2">
            Advanced — defines how Wes answers. Switching voice resets this unless you have edited it.
          </p>
          <textarea
            rows={10}
            value={promptDraft}
            onChange={(e) => { setPromptDraft(e.target.value); setPromptDirty(true); }}
            className="w-full border border-[var(--rule)] px-3 py-2 text-sm font-mono leading-relaxed"
          />
          {promptDirty && (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving}
              className="mt-2 inline-flex items-center gap-1.5 bg-[var(--slate)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save prompt
            </button>
          )}
        </div>

        <div className="border border-[var(--slate)] bg-[var(--slate-dim)]/60 px-3 py-2 flex gap-2 text-xs text-[var(--text-ter)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p className="m-0">
            To make Wes mode richer, add sample Q&amp;A, banned phrases, and product context to the system prompt above.
          </p>
        </div>
      </div>
    </div>
  );
}
